import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../flux/stores/hooks';
import { fetchProject } from '../../../../flux/actions/projectsThunks';
import { fetchJob, fetchJobResults } from '../../../../flux/actions/jobsThunks';
import { useApiAccessToken } from '../../../../utils/useApiAccessToken';
import { useJobsPolling } from '../../../../utils/useJobsPolling';
import { formatDateTime } from '../../../../utils/formatDate';
import { JOB_STATUS_PRESENTATION, TRIGGER_LABELS } from '../../../../utils/jobPresentation';
import { StatusBadge } from '../../../common/StatusBadge/StatusBadge';
import { JobResultsList } from '../JobResultsList/JobResultsList';

const ACTIVE_STATUSES = ['queued', 'running'];
const POLL_INTERVAL_MS = 5000;
const EMPTY_RESULTS = { status: 'idle', data: [], error: null };

export const ProjectCheckDetail = () => {
    const { projectId, jobId } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();

    const project = useAppSelector(state => state.projects.current.data);
    const jobs = useAppSelector(state => state.jobs.byProject[projectId]?.jobs);
    const resultsState = useAppSelector(state => state.jobs.byProject[projectId]?.results[jobId]) ?? EMPTY_RESULTS;

    const job = jobs?.data.find(entry => entry.object_id === jobId) ?? null;
    const [jobError, setJobError] = useState(null);

    const requestedRef = useRef(false);

    const withToken = useCallback(async (run) => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return null;
            return await run(accessToken);
        } catch (error) {
            console.error('Failed to acquire API token for this check:', error);
            return null;
        }
    }, [getAccessToken]);

    const loadJob = useCallback(async () => {
        const result = await withToken(accessToken => dispatch(fetchJob({ accessToken, projectId, jobId })));
        if (result && fetchJob.rejected.match(result)) setJobError(result.payload ?? { message: 'Failed to load this check.' });
        else if (result) setJobError(null);
    }, [withToken, dispatch, projectId, jobId]);

    const loadResults = useCallback(
        () => withToken(accessToken => dispatch(fetchJobResults({ accessToken, projectId, jobId }))),
        [withToken, dispatch, projectId, jobId]
    );

    useEffect(() => {
        if (requestedRef.current) return;
        requestedRef.current = true;
        withToken(accessToken => dispatch(fetchProject({ accessToken, projectId })));
        loadJob();
    }, [withToken, dispatch, projectId, loadJob]);

    const isActive = Boolean(job) && ACTIVE_STATUSES.includes(job.status);
    useJobsPolling(isActive, loadJob, POLL_INTERVAL_MS);

    // Results only exist once the job has finished its pass, and don't
    // change afterwards - fetch them once the job settles, not on every poll
    // tick while it's still queued/running, and not again once loaded.
    useEffect(() => {
        if (!job || isActive || resultsState.status !== 'idle') return;
        loadResults();
    }, [job, isActive, resultsState.status, loadResults]);

    if (!job && jobError) {
        return (
            <div className="page-content">
                <button type="button" className="pw-back" onClick={() => navigate(`/projects/${projectId}`)}>
                    ← Back to project
                </button>
                <div className="rules-empty">
                    <p>{jobError.status === 404 ? 'This check no longer exists.' : jobError.message ?? 'This check could not be loaded.'}</p>
                </div>
            </div>
        );
    }

    if (!job) {
        return (
            <div className="page-content">
                <p className="projects-loading">Loading check...</p>
            </div>
        );
    }

    const presentation = JOB_STATUS_PRESENTATION[job.status];

    return (
        <div className="page-content">
            <button type="button" className="pw-back" onClick={() => navigate(`/projects/${projectId}`)}>
                ← Back to {project?.name ?? 'project'}
            </button>

            <div className="pd-header">
                <div className="pd-header-main">
                    <h1 className="rules-title">Check</h1>
                    <StatusBadge label={presentation.label} variant={presentation.variant} />
                </div>
            </div>

            <dl className="pd-facts">
                <div className="pd-fact">
                    <dt>Trigger</dt>
                    <dd>{TRIGGER_LABELS[job.trigger_type] ?? job.trigger_type}</dd>
                </div>
                <div className="pd-fact">
                    <dt>Queued</dt>
                    <dd>{formatDateTime(job.queued_at)}</dd>
                </div>
                <div className="pd-fact">
                    <dt>Started</dt>
                    <dd>{formatDateTime(job.started_at)}</dd>
                </div>
                <div className="pd-fact">
                    <dt>Finished</dt>
                    <dd>{formatDateTime(job.finished_at)}</dd>
                </div>
                {job.error_code && (
                    <div className="pd-fact">
                        <dt>Error</dt>
                        <dd>{job.error_code}</dd>
                    </div>
                )}
            </dl>

            {resultsState.status === 'error' && (
                <div className="rules-alert rules-alert-error" role="alert">
                    <span>{resultsState.error?.message ?? 'Failed to load results.'}</span>
                    <button type="button" className="rules-btn-ghost" onClick={loadResults}>Retry</button>
                </div>
            )}

            {isActive ? (
                <p className="projects-loading">Results will appear once the check finishes.</p>
            ) : resultsState.status === 'idle' || resultsState.status === 'loading' ? (
                <p className="projects-loading">Loading results...</p>
            ) : (
                <JobResultsList results={resultsState.data} />
            )}
        </div>
    );
};

export default ProjectCheckDetail;
